import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Modal, Input, Textarea, Select } from "./Leads";
import { Plus, NotebookPen, Clock } from "lucide-react";
import { toast } from "sonner";

export default function DAR() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    project_id: "", project_name: "", client_name: "",
    work_done: "", hours: 0, pending_work: "",
    date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const [d, p] = await Promise.all([api.get("/dar"), api.get("/projects")]);
    setItems(d.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const project = projects.find((p) => p.id === form.project_id);
      await api.post("/dar", { ...form, hours: Number(form.hours), project_name: project?.project_name || "", client_name: project?.client_name || "" });
      toast.success("DAR entry added");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const totalHours = items.reduce((acc, i) => acc + (Number(i.hours) || 0), 0);
  const byUser = items.reduce((acc, i) => {
    acc[i.user_name] = (acc[i.user_name] || 0) + Number(i.hours || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Productivity</p>
          <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Daily Activity Report</h1>
          <p className="text-stone-500 mt-2">{items.length} entries · {totalHours} total hours</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-dar-btn" className="flex items-center gap-2 bg-stone-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium">
          <Plus className="w-4 h-4" /> Log Activity
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-xl p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-4">Recent activity</div>
          <div className="space-y-3">
            {items.map((d) => (
              <div key={d.id} data-testid={`dar-${d.id}`} className="border-l-2 border-emerald-600 pl-4 pb-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-stone-900">{d.user_name}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" />{d.hours}h</div>
                </div>
                <div className="text-xs text-stone-500 mb-2">{d.date} · {d.project_name || "—"} · {d.client_name || "—"}</div>
                <div className="text-sm text-stone-700">{d.work_done}</div>
                {d.pending_work && <div className="text-xs text-amber-700 mt-1">Pending: {d.pending_work}</div>}
              </div>
            ))}
            {items.length === 0 && <div className="text-sm text-stone-500 text-center py-8">No entries yet</div>}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-4">Productivity by member</div>
          <div className="space-y-3">
            {Object.entries(byUser).map(([n, h]) => (
              <div key={n}>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-stone-800">{n}</span>
                  <span className="text-sm font-semibold text-stone-900">{h}h</span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${Math.min((h / Math.max(totalHours, 1)) * 100, 100)}%` }} />
                </div>
              </div>
            ))}
            {Object.keys(byUser).length === 0 && <div className="text-sm text-stone-500">No data</div>}
          </div>
        </div>
      </div>

      {open && (
        <Modal title="Log Activity" onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Select label="Project" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} options={[{ value: "", label: "—" }, ...projects.map((p) => ({ value: p.id, label: p.project_name }))]} testid="dar-project" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required testid="dar-date" />
              <Input label="Hours" type="number" value={form.hours} onChange={(v) => setForm({ ...form, hours: v })} required testid="dar-hours" />
            </div>
            <Textarea label="Work Done" value={form.work_done} onChange={(v) => setForm({ ...form, work_done: v })} testid="dar-work" />
            <Textarea label="Pending Work" value={form.pending_work} onChange={(v) => setForm({ ...form, pending_work: v })} testid="dar-pending" />
            <button type="submit" data-testid="dar-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Save Entry</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
